// Thin react-query wrappers around the bot's API.
//
// Each hook re-fetches on an interval so the UI stays live without manual
// refresh. Intervals are conservative because the bot itself only ticks every
// ~30s; polling faster than that just shows the same data twice.
//
// Phase 6 follow-up: every hook is now strategy-scoped via
// useSelectedStrategy(). When the user picks a different strategy in the
// header dropdown, the query key changes and react-query refetches.
import { useQuery } from '@tanstack/react-query';
import {
  authApi,
  dashboardApi,
  deploymentsApi,
  epochsApi,
  portfolioApi,
  strategiesApi,
  walletsApi,
  type PortfolioRange,
} from '../lib/api';
import { useSelectedStrategy } from './useSelectedStrategy';
import { useInvestorFilter } from '../lib/investor-filter';

const POLL_FAST = 5_000; // 5s — header / KPIs / counts
const POLL_MED = 10_000; // 10s — actions, charts (lots of data, rarely changes)

export function useState() {
  const { selectedStrategyId } = useSelectedStrategy();
  return useQuery({
    queryKey: ['state', selectedStrategyId],
    queryFn: () => dashboardApi.state(selectedStrategyId),
    refetchInterval: POLL_FAST,
  });
}

export function useSnapshots(hours = 24, points = 500) {
  const { selectedStrategyId } = useSelectedStrategy();
  return useQuery({
    queryKey: ['snapshots', hours, points, selectedStrategyId],
    queryFn: () => dashboardApi.snapshots(hours, points, selectedStrategyId),
    refetchInterval: POLL_MED,
  });
}

export function useActions(limit = 50) {
  const { selectedStrategyId } = useSelectedStrategy();
  return useQuery({
    queryKey: ['actions', limit, selectedStrategyId],
    queryFn: () => dashboardApi.actions(limit, selectedStrategyId),
    refetchInterval: POLL_MED,
  });
}

/** Paginated + filterable actions for the expandable table. */
export function usePagedActions(opts: {
  kind?: string;
  status?: string;
  fromTsMs?: number;
  toTsMs?: number;
  limit: number;
  offset: number;
}) {
  const { selectedStrategyId } = useSelectedStrategy();
  return useQuery({
    queryKey: [
      'actions-paged',
      selectedStrategyId,
      opts.kind,
      opts.status,
      opts.fromTsMs,
      opts.toTsMs,
      opts.limit,
      opts.offset,
    ],
    queryFn: () =>
      dashboardApi.actionsPaged({
        ...opts,
        strategyId: selectedStrategyId,
      }),
    refetchInterval: POLL_MED,
    // Keep previous data visible while a new page loads — prevents the
    // table from flickering empty during pagination.
    placeholderData: (prev) => prev,
  });
}

export function useDaily(days = 30) {
  const { selectedStrategyId } = useSelectedStrategy();
  return useQuery({
    queryKey: ['daily', days, selectedStrategyId],
    queryFn: () => dashboardApi.daily(days, selectedStrategyId),
    // Day buckets only change at UTC midnight + on each new tick. Slow poll.
    refetchInterval: 60_000,
  });
}

export function useIncome() {
  const { selectedStrategyId } = useSelectedStrategy();
  return useQuery({
    queryKey: ['income', selectedStrategyId],
    queryFn: () => dashboardApi.income(selectedStrategyId),
    refetchInterval: POLL_MED,
  });
}

export function useRisk(days = 30) {
  const { selectedStrategyId } = useSelectedStrategy();
  return useQuery({
    queryKey: ['risk', days, selectedStrategyId],
    queryFn: () => dashboardApi.risk(days, selectedStrategyId),
    refetchInterval: 60_000,
  });
}

export function useStrategies() {
  return useQuery({
    queryKey: ['strategies'],
    queryFn: strategiesApi.list,
    refetchInterval: 15_000,
  });
}

export function useWallets() {
  return useQuery({
    queryKey: ['wallets'],
    queryFn: walletsApi.list,
    refetchInterval: 15_000,
  });
}

export function useCapitalEvents(id: number | null | undefined) {
  return useQuery({
    queryKey: ['capital-events', id],
    queryFn: () => strategiesApi.capitalEvents(id!),
    enabled: id != null,
    refetchInterval: 30_000,
  });
}

// ── Investor portfolio hooks (aHedge customer dashboard) ──────────────
// Scoped to the shared strategy filter (null/undefined = all active).
// The queryIds are part of the key so switching the filter refetches.

export function usePortfolioSummary() {
  const { queryIds } = useInvestorFilter();
  return useQuery({
    queryKey: ['portfolio-summary', queryIds ?? 'all'],
    queryFn: () => portfolioApi.summary(queryIds),
    refetchInterval: POLL_FAST,
  });
}

export function usePortfolioNav(range: PortfolioRange = '7d') {
  const { queryIds } = useInvestorFilter();
  return useQuery({
    queryKey: ['portfolio-nav', range, queryIds ?? 'all'],
    queryFn: () => portfolioApi.nav(range, queryIds),
    refetchInterval: POLL_MED,
  });
}

export function usePortfolioPositions() {
  const { queryIds } = useInvestorFilter();
  return useQuery({
    queryKey: ['portfolio-positions', queryIds ?? 'all'],
    queryFn: () => portfolioApi.positions(queryIds),
    refetchInterval: POLL_MED,
  });
}

export function usePortfolioPnl() {
  const { queryIds } = useInvestorFilter();
  return useQuery({
    queryKey: ['portfolio-pnl', queryIds ?? 'all'],
    queryFn: () => portfolioApi.pnl(queryIds),
    refetchInterval: POLL_MED,
  });
}

export function useCurrentUser() {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: authApi.me,
    retry: false, // 401 should bubble immediately to RouteGuard
    staleTime: 60_000,
  });
}

/**
 * Poll a strategy's active deployment. Returns null when there is
 * no in-flight deployment. Used by the Deployment Progress view.
 * Polls every 2 seconds while a deployment is active.
 */
export function useActiveDeployment(strategyId: number | null | undefined) {
  return useQuery({
    queryKey: ['deployment-active', strategyId],
    queryFn: () =>
      deploymentsApi.active(strategyId!).catch((err) => {
        // 404 = no active deployment, treat as null instead of error
        if (err instanceof Error && err.message.includes('404')) return null;
        throw err;
      }),
    enabled: strategyId != null,
    refetchInterval: 2_000,
  });
}

/** Single deployment by id — useful when navigating directly to a deployment URL. */
export function useDeployment(id: number | null | undefined) {
  return useQuery({
    queryKey: ['deployment', id],
    queryFn: () => deploymentsApi.get(id!),
    enabled: id != null,
    refetchInterval: 2_000,
  });
}

/**
 * LP epoch history for a strategy. One row per LP NFT minted, with
 * full mint→close P&L breakdown. Polls every 30s — epochs change
 * only on mint/redeploy events.
 */
export function useEpochs(
  strategyId: number | null | undefined,
  limit = 50,
  offset = 0,
) {
  return useQuery({
    queryKey: ['epochs', strategyId, limit, offset],
    queryFn: () => epochsApi.list(strategyId!, limit, offset),
    enabled: strategyId != null,
    refetchInterval: 30_000,
  });
}
